import React from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import CounselorInfo from './CounselorInfo';

function formatApptDateTime(dateString) {
  if (!dateString) return null;
  const date = new Date(dateString);

  // Convert to Central Time (America/Chicago)
  const options = {
    timeZone: 'America/Chicago',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  };

  // Format: Month DD, YYYY at hh:mm AM/PM CT
  const parts = new Intl.DateTimeFormat('en-US', options).formatToParts(date);
  const month = parts.find(p => p.type === 'month').value;
  const day = parts.find(p => p.type === 'day').value;
  const year = parts.find(p => p.type === 'year').value;
  const hour = parts.find(p => p.type === 'hour').value;
  const minute = parts.find(p => p.type === 'minute').value;
  const dayPeriod = parts.find(p => p.type === 'dayPeriod').value;

  return `${month} ${day}, ${year}, at ${hour}:${minute} ${dayPeriod} CT`;
}

export default function MeetingDetails({ hubCards }) {
  const caseStatusState = useSelector(state => state?.ch31CaseStatusDetails);

  const orientationAppointmentDetails =
    caseStatusState?.data?.attributes?.orientationAppointmentDetails;
  const appointmentScheduled =
    orientationAppointmentDetails?.appointmentDateTime;

  return (
    <>
      {!caseStatusState?.data?.attributes?.hasVeteranOptedForEva && (
        <div className="usa-width-one-whole vads-u-margin-bottom--2">
          <va-alert-expandable
            status="info"
            trigger="You aren’t opted into e-VA"
          >
            <p>
              e-VA is a communication tool for VR&E that helps you and your
              counselor stay connected by text or email. To opt in, ask your
              counselor to email a new invitation link. You can still schedule
              and communicate with your counselor if you decide not to opt in.
            </p>
          </va-alert-expandable>
        </div>
      )}
      {appointmentScheduled ? (
        <>
          <h3>Meeting scheduled</h3>
          <p>
            We scheduled your initial evaluation counselor meeting for&nbsp;
            {formatApptDateTime(
              orientationAppointmentDetails.appointmentDateTime,
            )}{' '}
            via Microsoft Teams.
          </p>
          <p>
            If you prefer to have an in-person meeting, contact your counselor.
          </p>
        </>
      ) : (
        <p>
          Your counselor is scheduling your initial evaluation counselor
          meeting. Once scheduled, we'll provide your meeting details on this
          page.
        </p>
      )}

      <CounselorInfo />

      {appointmentScheduled && (
        <>
          <h3>What to expect during your initial evaluation meeting</h3>
          <p>
            During your meeting, your counselor will ask for information that
            may help us make a decision about your VR&E benefits. Your counselor
            will ask you for this type of information:
          </p>
          <ul>
            <li>
              Your work history, job interests, past training, and education
            </li>
            <li>
              How your disabilities affect your ability to work or live
              independently
            </li>
            <li>Your career assessment results</li>
          </ul>
          <p>
            You can also ask your counselor any questions you have about VR&E
            benefits.
          </p>
          <h3>Documents to bring to your meeting</h3>
          <p>
            Gather supporting documents to bring to your meeting. This could
            include any of these types of documents:
          </p>
          <ul>
            <li>A resume</li>
            <li>Training records or transcripts</li>
            <li>Job certifications or licenses</li>
            <li>A current position description</li>
            <li>Recent non-VA medical records</li>
            <li>Your job search history</li>
            <li>
              Any documents that show challenges you’ve experienced in obtaining
              or maintaining employment
            </li>
          </ul>
        </>
      )}
      {hubCards}
    </>
  );
}

MeetingDetails.propTypes = {
  hubCards: PropTypes.node,
};
